// payment_controller.js
import {Controller} from "stimulus"

export default class extends Controller {
    static targets = []

    // Every type AccountsPayable::Payment#payment_types offers, so no
    // selectable method falls through to the raw class name.
    static methodLabels = {
        'Bank::Check': { singular: 'Check', plural: 'Checks' },
        'Bank::Eft': { singular: 'EFT', plural: 'EFTs' },
        'Bank::CardPayment': { singular: 'Card Payment', plural: 'Card Payments' }
    }

    connect() {
    }

    labelsFor(method) {
        return this.constructor.methodLabels[method] || { singular: method, plural: `${method}s` }
    }

    methodLabel(method, count) {
        const labels = this.labelsFor(method)
        return count === 1 ? labels.singular : labels.plural
    }

    // The payment type's name, for headings, where no count applies.
    methodName(method) {
        return this.labelsFor(method).singular
    }

    showSummary() {
        const selectedPayments = document.querySelectorAll('.payment-select')
        const paymentsByMethod = {}
        const vendorAccounts = {}

        selectedPayments.forEach(select => {
            if (select.value && select.value !== '' && select.value !== 'schedule_later') {
                const method = select.value
                const amount = parseFloat(select.dataset.paymentAmount)
                const vendorId = select.dataset.vendorId
                const vendorName = select.dataset.vendorName
                const paymentId = select.dataset.paymentId
                // Identifies the bank account the payment is drawn on.
                // Built server-side so this count matches the number of
                // disbursements batch processing will actually create.
                const accountKey = select.dataset.accountKey

                if (!paymentsByMethod[method]) {
                    paymentsByMethod[method] = {
                        total: 0,
                        payments: [],
                        count: 0,
                        vendors: new Set()
                    }
                }

                paymentsByMethod[method].total += amount
                paymentsByMethod[method].payments.push(paymentId)
                paymentsByMethod[method].count++
                paymentsByMethod[method].vendors.add(vendorId)

                // Track bank accounts per vendor+method to detect splits
                const vendorMethodKey = `${vendorId}::${method}`
                if (!vendorAccounts[vendorMethodKey]) {
                    vendorAccounts[vendorMethodKey] = {
                        vendorName: vendorName,
                        method: method,
                        accounts: new Map()
                    }
                }
                const currentAmount = vendorAccounts[vendorMethodKey].accounts.get(accountKey) || 0
                vendorAccounts[vendorMethodKey].accounts.set(accountKey, currentAmount + amount)
            }
        })

        // Detect vendors being paid from more than one bank account
        const multiAccountVendors = Object.values(vendorAccounts)
            .filter(v => v.accounts.size > 1)

        // Count actual number of checks/EFTs that will be created per method
        const methodGroupCounts = {}
        Object.values(vendorAccounts).forEach(v => {
            if (!methodGroupCounts[v.method]) {
                methodGroupCounts[v.method] = 0
            }
            methodGroupCounts[v.method] += v.accounts.size
        })

        const summaryContainer = document.getElementById('payment-summary-sections')
        summaryContainer.textContent = ''

        // The notice is a live region rendered empty by the view, so its
        // content is announced when injected. Clear it before rebuilding.
        const notice = document.getElementById('multi-account-notice')
        notice.textContent = ''
        notice.className = ''

        if (multiAccountVendors.length > 0) {
            notice.className = 'bg-amber-50 border border-amber-300 rounded-md p-3 mb-4'

            const heading = document.createElement('p')
            heading.className = 'font-medium text-amber-800 mb-1'
            heading.textContent = 'These vendors are being paid from more than one bank account, so more than one payment will be created for each:'
            notice.appendChild(heading)

            const list = document.createElement('ul')
            list.className = 'text-base sm:text-sm text-amber-700 list-disc pl-5'

            multiAccountVendors.forEach(v => {
                const count = v.accounts.size
                const label = this.methodLabel(v.method, count)
                const item = document.createElement('li')

                const vendorStrong = document.createElement('strong')
                vendorStrong.textContent = v.vendorName
                item.appendChild(vendorStrong)
                item.appendChild(document.createTextNode(
                    ` is paid from ${count} different bank accounts \u2014 ${count} ${label} will be created`
                ))
                list.appendChild(item)
            })

            notice.appendChild(list)
        }

        // Build method summary using DOM APIs
        Object.entries(paymentsByMethod).forEach(([method, data]) => {
            const groupCount = methodGroupCounts[method] || data.vendors.size
            // A "Number of ..." heading takes the plural whatever the count,
            // so it does not agree with the count the way the notice does.
            const label = this.labelsFor(method).plural

            const section = document.createElement('div')
            section.className = 'border-b pb-4 mb-4'

            const title = document.createElement('h4')
            title.className = 'font-medium mb-2'
            title.textContent = this.methodName(method)
            section.appendChild(title)

            const details = document.createElement('div')
            details.className = 'text-base sm:text-sm text-gray-600 tabular-nums'

            const formattedTotal = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD'
            }).format(data.total)

            details.appendChild(document.createTextNode(`Number of Payments: ${data.count}`))
            details.appendChild(document.createElement('br'))
            details.appendChild(document.createTextNode(`Number of ${label}: ${groupCount}`))
            details.appendChild(document.createElement('br'))
            details.appendChild(document.createTextNode(`Total Amount: ${formattedTotal}`))

            section.appendChild(details)
            summaryContainer.appendChild(section)
        })

        // Add hidden inputs for form submission using DOM APIs
        const selectedPaymentsDiv = document.getElementById('selected-payments')
        selectedPaymentsDiv.textContent = ''

        Object.entries(paymentsByMethod).forEach(([method, data]) => {
            data.payments.forEach(paymentId => {
                const input = document.createElement('input')
                input.type = 'hidden'
                input.name = `payments[${paymentId}]`
                input.value = method
                selectedPaymentsDiv.appendChild(input)
            })
        })
    }
}
